import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import usePermissions from '../../hooks/usePermissions';

/**
 * EditPermissionModal component
 * Modal for editing permission name and description (path is immutable)
 */
const EditPermissionModal = ({ open, permission, onClose, onSuccess }) => {
  const { updatePermission, loading } = usePermissions();

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when modal opens or permission changes
  useEffect(() => {
    if (open && permission) {
      setFormData({
        name: permission.name || '',
        description: permission.description || ''
      });
      setErrors({});
      setSubmitError(null);
      setHasChanges(false);
    }
  }, [open, permission]);

  // Check if form has changes
  useEffect(() => {
    if (permission) {
      const nameChanged = formData.name !== (permission.name || '');
      const descChanged = formData.description !== (permission.description || '');
      setHasChanges(nameChanged || descChanged);
    }
  }, [formData, permission]);

  // Handle form field changes
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: '' }));
    setSubmitError(null);
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      setSubmitError(null);
      await updatePermission(permission.id, formData);
      onSuccess();
    } catch (err) {
      setSubmitError(err.message || err || 'Failed to update permission');
    }
  };

  if (!permission) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit Permission
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          Category: {permission.category === 'system' ? 'System' : 'Project'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Immutable Path Display */}
          <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LockIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Permission Path (Immutable)
              </Typography>
            </Box>
            <Chip
              label={permission.path}
              size="medium"
              sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}
            />
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              The permission path cannot be changed after creation to maintain data integrity.
            </Typography>
          </Box>

          {/* Name Field */}
          <TextField
            label="Permission Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange('name')}
            error={Boolean(errors.name)}
            helperText={errors.name || 'Display name for the permission'}
            placeholder="e.g., Dashboard, User Management, Project Settings"
          />

          {/* Description Field */}
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange('description')}
            error={Boolean(errors.description)}
            helperText={errors.description || 'Optional description of what this permission controls'}
            placeholder="Describe what this permission allows users to do..."
          />

          {/* Core Permission Warning */}
          {permission.is_core && (
            <Alert severity="info">
              <Typography variant="caption">
                <strong>Core Permission:</strong> This is a core permission seeded from specification files.
                While you can edit the name and description, it's recommended to keep them aligned with the original specification.
              </Typography>
            </Alert>
          )}

          {/* Usage Info */}
          {permission.usage_count > 0 && (
            <Alert severity="warning">
              <Typography variant="caption">
                <strong>In Use:</strong> This permission is currently assigned to {permission.usage_count} role(s).
                Changes to the name and description will be reflected in all roles using this permission.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !hasChanges}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPermissionModal;
